import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { NgxOtpStyleAdderMethod } from '@core/enums';
import { NgxOtpInputComponent } from 'ngx-otp-input';

import { APP_CONFIG } from '../environments/environment';

import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

// ngx-otp-input bug workaround: handleDisable runs both inside the [disable] setter
// and again in ngAfterViewInit, double-appending classList.inputDisabled into the
// component's `styles` 2-D array. The resulting className changes between CD passes
// and triggers NG0100. Dedupe entries after every addStyleToAll/addStyle call so
// the second append is a no-op.
//
// This reaches into library internals, so every step is guarded: if a future
// ngx-otp-input release renames/removes these methods the patch silently no-ops
// instead of throwing at bootstrap.
type StyleAdder = (this: { styles?: string[][] }, ...args: unknown[]) => void;
const dedupeStyles = (styles: string[][] | undefined): void => {
    if (!Array.isArray(styles)) { return; }
    for (let i = 0; i < styles.length; i++) {
        if (Array.isArray(styles[i])) {
            styles[i] = Array.from(new Set(styles[i]));
        }
    }
};
const patchStyleAdder = (proto: Record<string, unknown>, methodName: NgxOtpStyleAdderMethod): void => {
    const original = proto[methodName];
    if (typeof original !== 'function') { return; }
    const originalFn = original as StyleAdder;
    proto[methodName] = function patchedStyleAdder(this: { styles?: string[][] }, ...args: unknown[]) {
        originalFn.apply(this, args);
        dedupeStyles(this.styles);
    } as StyleAdder;
};
const otpProto = NgxOtpInputComponent?.prototype as unknown as Record<string, unknown> | undefined;
if (otpProto) {
    patchStyleAdder(otpProto, NgxOtpStyleAdderMethod.AddStyleToAll);
    patchStyleAdder(otpProto, NgxOtpStyleAdderMethod.AddStyle);
}

if (APP_CONFIG.production) {
    enableProdMode();
}

if (APP_CONFIG.SILENCE_CONSOLE) {
    // Console output is silenced on the production stage on purpose; the no-op replacements are the
    // point. A test deployment keeps its console so a tester can read it.
    /* eslint-disable @typescript-eslint/no-empty-function */
    const silent = (): void => {};
    window.console.log = silent;
    window.console.warn = silent;
    window.console.error = silent;
    window.console.time = silent;
    window.console.timeEnd = silent;
    /* eslint-enable @typescript-eslint/no-empty-function */
}

bootstrapApplication(AppComponent, appConfig)
    .catch(err => console.error(err));
